import type { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import { ColorSystemPositive5 } from 'autogen/design-tokens/tokens';
import UncontrolledToggleButton from 'components/generic/Toggle/components/UncontrolledToggleButton';
import Tag from 'components/generic/tag';
import { Typography } from 'components/generic/typography';
import Status from 'components/Status';
import useFeatureToggles from 'hooks/user/useFeatureToggles';
import { GA4Tracking } from 'modules/tracking/GA4';

export type ClickAndCollectStoresToggleProps = {
    showClickAndCollectOnly: boolean;
    setShowClickAndCollectOnly: Dispatch<SetStateAction<boolean>>;
    clickAndCollectLabel?: string;
    clickAndCollectTimeTag?: string;
    showBullet?: boolean;
};

const ClickAndCollectToggle = styled.div`
    display: flex;
    justify-content: end;
    align-items: center;
    gap: ${({ theme }) => theme.Spacing8};
    margin-bottom:  ${({ theme }) => theme.Spacing8};
`;

export default function ClickAndCollectStoresToggle(props: ClickAndCollectStoresToggleProps) {
    const {
        showClickAndCollectOnly,
        setShowClickAndCollectOnly,
        clickAndCollectLabel,
        clickAndCollectTimeTag,
        showBullet = true
    } = props;
    const { clickAndCollectTimePromiseInLabelDesignEnabled } = useFeatureToggles(); // This feature toggle is used to determine if the green C&C availablity bullet should be replaced with a time tag, e.g. "2T"

    function handleClickAndCollectToggle() {
        setShowClickAndCollectOnly((prevState) => !prevState);

        GA4Tracking({
            eventName: 'toggle_click_and_collect',
            context: 'delivery',
            category: 'ecommerce',
            eventParams: [{ param_name: 'toggle', param_value: !showClickAndCollectOnly }]
        });
    }

    function bulletRenderer() {
        if (clickAndCollectTimePromiseInLabelDesignEnabled && clickAndCollectTimeTag) {
            return <Tag text={clickAndCollectTimeTag} />;
        }

        if (showBullet && !clickAndCollectTimePromiseInLabelDesignEnabled) {
            return <Status color="Green" size={12} />;
        }

        return null;
    }

    return (
        <ClickAndCollectToggle>
            {bulletRenderer()}
            <Typography.Body size="M" strong as="span">
                {clickAndCollectLabel}
            </Typography.Body>
            <UncontrolledToggleButton
                initialValue={showClickAndCollectOnly}
                onChange={handleClickAndCollectToggle}
                size="small"
            />
        </ClickAndCollectToggle>
    );
}
